import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  applyRmsMarketReference,
  createRmsMarketReference,
  fetchRmsMarketReference,
  fetchRmsMarketReferenceRoomRates,
  fetchRmsMarketReferences,
  previewRmsMarketReference,
} from "./rmsApi";
import {
  getApprovableMarketReferenceDates,
  normalizeMarketReferenceDateSelection,
} from "./rmsMarketReferenceSelection";

const SOURCE_COLORS = [
  "#6f82ad",
  "#7997b7",
  "#54749b",
  "#7d8fa8",
  "#4d8d99",
  "#8196c4",
];

function getErrorMessage(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback;
}

function formatMoney(value, currency = "EUR", locale = "es-ES") {
  if (value == null || value === "" || !Number.isFinite(Number(value))) return "-";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency || "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  } catch (_error) {
    return `${Number(value).toFixed(2)} ${currency || "EUR"}`;
  }
}

function formatShortDate(value, locale = "es-ES") {
  if (!value) return "-";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(locale, { day: "2-digit", month: "short" });
}

function buildFormulaLabel(reference, t) {
  const combineLabels = {
    average: t("Average"),
    min: t("Minimum"),
    max: t("Maximum"),
  };
  if (reference?.adjustment_type === "equal") {
    return t("{method}, with no adjustment", {
      method: combineLabels[reference?.combine_method] || reference?.combine_method,
    });
  }

  const direction = reference?.adjustment_type === "above" ? t("above") : t("below");
  const value = Number(reference?.adjustment_value || 0);
  const suffix = reference?.adjustment_unit === "percent" ? "%" : reference?.currency;
  return t("{method}, {value}{suffix} {direction}", {
    method: combineLabels[reference?.combine_method] || reference?.combine_method,
    value,
    suffix: suffix ? ` ${suffix}` : "",
    direction,
  });
}

function MarketReferenceChart({ reference, locale, t }) {
  const days = useMemo(
    () => (Array.isArray(reference?.days) ? reference.days : []),
    [reference?.days]
  );
  const sources = useMemo(
    () =>
      Array.isArray(reference?.selected_sources) ? reference.selected_sources : [],
    [reference?.selected_sources]
  );
  const chartData = useMemo(
    () =>
      days.map((day) => {
        const row = {
          stay_date: day.stay_date,
          own_price: day.own_price ?? day.own_price_before ?? null,
          reference_price: day.reference_price,
          calculated_price: day.calculated_price,
        };
        const priceByKey = new Map(
          (Array.isArray(day.source_prices) ? day.source_prices : []).map((item) => [
            item.key,
            item.price,
          ])
        );
        sources.forEach((source, index) => {
          row[`source_${index}`] = priceByKey.get(source.key) ?? null;
        });
        return row;
      }),
    [days, sources]
  );

  if (!chartData.length) return null;

  return (
    <div className="rmo-marketRefChart" aria-label={t("Market price comparison chart")}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 12, right: 18, left: 2, bottom: 4 }}>
          <CartesianGrid stroke="#d7e2ec" strokeDasharray="3 4" vertical={false} />
          <XAxis
            dataKey="stay_date"
            tickFormatter={(value) => formatShortDate(value, locale)}
            stroke="#70809a"
            tick={{ fill: "#5b6680", fontSize: 11 }}
            minTickGap={24}
          />
          <YAxis
            stroke="#70809a"
            tick={{ fill: "#5b6680", fontSize: 11 }}
            tickFormatter={(value) => Number(value).toFixed(0)}
            width={48}
          />
          <Tooltip
            labelFormatter={(value) => formatShortDate(value, locale)}
            formatter={(value, name) => [
              formatMoney(value, reference?.currency, locale),
              name,
            ]}
            contentStyle={{
              border: "1px solid #bdcad8",
              borderRadius: 10,
              background: "#ffffff",
              color: "#172653",
              fontSize: 12,
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11, color: "#26354f" }} />
          <Line
            type="monotone"
            dataKey="own_price"
            name={t("My current price")}
            stroke="#223a7a"
            strokeWidth={2.4}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
          {sources.map((source, index) => (
            <Line
              key={source.key || index}
              type="monotone"
              dataKey={`source_${index}`}
              name={source.label || t("Competitor offer")}
              stroke={SOURCE_COLORS[index % SOURCE_COLORS.length]}
              strokeWidth={1.35}
              strokeDasharray="4 3"
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
          <Line
            type="monotone"
            dataKey="reference_price"
            name={t("Combined reference")}
            stroke="#3db6e9"
            strokeWidth={2.2}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="calculated_price"
            name={t("Resulting price")}
            stroke="#31969d"
            strokeWidth={2.8}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ReferenceDaysTable({
  reference,
  locale,
  t,
  selectedDates = [],
  onToggleDate = null,
}) {
  const days = Array.isArray(reference?.days) ? reference.days : [];
  const selectedDateSet = new Set(selectedDates);
  const selectable = typeof onToggleDate === "function";
  if (!days.length) return null;

  return (
    <div className="rmo-tableWrap rmo-marketRefTableWrap">
      <table className="rmo-table rmo-marketRefTable">
        <thead>
          <tr>
            {selectable ? <th>{t("Approve")}</th> : null}
            <th>{t("Stay date")}</th>
            <th>{t("My current price")}</th>
            <th>{t("Combined reference")}</th>
            <th>{t("Resulting price")}</th>
            <th>{t("Coverage")}</th>
            {reference?.status ? <th>{t("Application")}</th> : null}
          </tr>
        </thead>
        <tbody>
          {days.map((day) => {
            const sourcePrices = Array.isArray(day.source_prices) ? day.source_prices : [];
            const available = sourcePrices.filter((item) => item.price != null).length;
            const total = sourcePrices.length || day.selected_sources || 0;
            const canSelect =
              day.calculated_price != null && day.application_status !== "applied";
            return (
              <tr key={day.stay_date}>
                {selectable ? (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedDateSet.has(day.stay_date)}
                      disabled={!canSelect}
                      onChange={() => onToggleDate(day.stay_date)}
                      aria-label={t("Approve {date}", {
                        date: formatShortDate(day.stay_date, locale),
                      })}
                    />
                  </td>
                ) : null}
                <td><strong>{formatShortDate(day.stay_date, locale)}</strong></td>
                <td>{formatMoney(day.own_price ?? day.own_price_before, reference.currency, locale)}</td>
                <td>{formatMoney(day.reference_price, reference.currency, locale)}</td>
                <td><strong>{formatMoney(day.calculated_price, reference.currency, locale)}</strong></td>
                <td>{available}/{total}</td>
                {reference?.status ? (
                  <td>
                    <span className={`rmo-marketRefStatus is-${day.application_status || "pending"}`}>
                      {t(day.application_status || "pending")}
                    </span>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function RmsMarketPriceReferenceBuilder({
  businessUnitId,
  availableSeries,
  selectedSeriesKeys,
  onSelectedSeriesKeysChange,
  dateFrom,
  dateTo,
  canApplyMarketPrices,
  t,
  locale,
  onSaved,
}) {
  const [roomRates, setRoomRates] = useState([]);
  const [roomRatesLoading, setRoomRatesLoading] = useState(true);
  const [form, setForm] = useState({
    name: "",
    room_rate_id: "",
    date_from: dateFrom || "",
    date_to: dateTo || "",
    combine_method: "average",
    adjustment_type: "equal",
    adjustment_unit: "absolute",
    adjustment_value: "0",
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [progress, setProgress] = useState(null);

  const selectedSeries = useMemo(() => {
    const selectedKeys = new Set(selectedSeriesKeys);
    return availableSeries.filter((series) => selectedKeys.has(series.key));
  }, [availableSeries, selectedSeriesKeys]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setRoomRatesLoading(true);
        const rows = await fetchRmsMarketReferenceRoomRates(businessUnitId);
        if (cancelled) return;
        setRoomRates(rows);
        setForm((current) => ({
          ...current,
          room_rate_id:
            current.room_rate_id && rows.some((row) => String(row.id) === String(current.room_rate_id))
              ? current.room_rate_id
              : rows[0]?.id
                ? String(rows[0].id)
                : "",
        }));
      } catch (nextError) {
        if (!cancelled) {
          setError(getErrorMessage(nextError, t("Could not load destination room rates.")));
        }
      } finally {
        if (!cancelled) setRoomRatesLoading(false);
      }
    };
    if (businessUnitId) load();
    return () => {
      cancelled = true;
    };
  }, [businessUnitId, t]);

  useEffect(() => {
    setForm((current) => ({
      ...current,
      date_from: current.date_from || dateFrom || "",
      date_to: current.date_to || dateTo || "",
    }));
  }, [dateFrom, dateTo]);

  useEffect(() => {
    setPreview(null);
    setNotice("");
  }, [selectedSeriesKeys]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setPreview(null);
    setNotice("");
  };

  const payload = useMemo(
    () => ({
      business_unit_id: businessUnitId,
      name: form.name,
      room_rate_id: Number(form.room_rate_id),
      date_from: form.date_from,
      date_to: form.date_to,
      combine_method: form.combine_method,
      adjustment_type: form.adjustment_type,
      adjustment_unit: form.adjustment_unit,
      adjustment_value: Number(form.adjustment_value || 0),
      selected_sources: selectedSeries,
    }),
    [businessUnitId, form, selectedSeries]
  );

  const validate = () => {
    if (!selectedSeries.length) return t("Select at least one exact competitor offer.");
    if (!form.room_rate_id) return t("Select a destination room rate.");
    if (!form.date_from || !form.date_to || form.date_from > form.date_to) {
      return t("Select a valid date range.");
    }
    return "";
  };

  const handlePreview = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      setBusy("preview");
      setError("");
      setNotice("");
      const nextPreview = await previewRmsMarketReference(payload);
      setPreview(nextPreview);
    } catch (nextError) {
      setError(getErrorMessage(nextError, t("Could not calculate the market reference.")));
    } finally {
      setBusy("");
    }
  };

  const handleSave = async (shouldApply) => {
    if (!canApplyMarketPrices) {
      setError(t("You do not have permission to save or apply market prices."));
      return;
    }
    if (!preview) {
      setError(t("Preview the result before saving it."));
      return;
    }

    try {
      setBusy(shouldApply ? "apply" : "save");
      setError("");
      setNotice("");
      setProgress(null);
      const savedReference = preview?.id
        ? preview
        : await createRmsMarketReference(payload);
      let finalReference = savedReference;
      let applicationErrors = [];

      if (shouldApply) {
        const stayDates = getApprovableMarketReferenceDates(savedReference);
        const result = await applyRmsMarketReference(savedReference, {
          stayDates,
          onProgress: setProgress,
        });
        finalReference = result.reference;
        applicationErrors = result.errors;
      }

      setPreview(finalReference);
      setNotice(
        shouldApply
          ? applicationErrors.length
            ? t("The reference was saved, but {count} dates could not be applied.", {
                count: applicationErrors.length,
              })
            : t("The reference was saved and its prices were applied.")
          : t("The market reference was saved without changing prices.")
      );
      onSaved?.(finalReference);
    } catch (nextError) {
      setError(getErrorMessage(nextError, t("Could not save the market reference.")));
    } finally {
      setBusy("");
      setProgress(null);
    }
  };

  const selectedRoomRate = roomRates.find(
    (row) => String(row.id) === String(form.room_rate_id)
  );
  const previewHasPendingDays = Boolean(
    preview?.days?.some(
      (day) => day.calculated_price != null && day.application_status !== "applied"
    )
  );
  const previewDaysWithReference =
    preview?.summary?.days_with_reference ??
    (Array.isArray(preview?.days)
      ? preview.days.filter((day) => day.reference_price != null).length
      : 0);

  return (
    <div className="rmo-sectionStack">
      <div className="rmo-surface rmo-marketRefBuilder">
        <div className="rmo-head">
          <div>
            <h3 className="rmo-headTitle">{t("Create a market price reference")}</h3>
            <div className="rmo-headSubtitle">
              {t("Choose exact competitor offers, combine them and decide how your own rate should be positioned.")}
            </div>
          </div>
          <span className="rmo-marketRefStepBadge">{t("Manual mode")}</span>
        </div>

        <div className="rmo-marketRefFlow">
          <section className="rmo-marketRefStep">
            <div className="rmo-marketRefStepHead">
              <span>1</span>
              <div>
                <strong>{t("Competitor offers")}</strong>
                <small>{t("Only the exact offers selected in Observed prices are used.")}</small>
              </div>
            </div>
            {selectedSeries.length ? (
              <div className="rmo-marketRefSourceList">
                {selectedSeries.map((series) => (
                  <div key={series.key} className="rmo-marketRefSourceChip">
                    <span>{series.label}</span>
                    <button
                      type="button"
                      onClick={() =>
                        onSelectedSeriesKeysChange(
                          selectedSeriesKeys.filter((key) => key !== series.key)
                        )
                      }
                      aria-label={t("Remove {name}", { name: series.label })}
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rmo-marketRefEmptyHint">
                {t("Go to Observed prices and select at least one competitor offer.")}
              </div>
            )}
          </section>

          <section className="rmo-marketRefStep">
            <div className="rmo-marketRefStepHead">
              <span>2</span>
              <div>
                <strong>{t("Destination and dates")}</strong>
                <small>{t("Choose the base room rate whose prices may be changed.")}</small>
              </div>
            </div>
            <div className="rmo-marketRefFormGrid">
              <label className="rmo-formField rmo-marketRefWideField">
                <span>{t("Reference name")}</span>
                <input
                  className="rmo-input"
                  value={form.name}
                  onChange={(event) => updateForm("name", event.target.value)}
                  placeholder={t("Example: Flexible BAR vs direct competitors")}
                />
              </label>
              <label className="rmo-formField rmo-marketRefWideField">
                <span>{t("Destination room rate")}</span>
                <select
                  className="rmo-select"
                  value={form.room_rate_id}
                  disabled={roomRatesLoading}
                  onChange={(event) => updateForm("room_rate_id", event.target.value)}
                >
                  {!roomRates.length ? <option value="">{t("No base room rates available")}</option> : null}
                  {roomRates.map((rate) => (
                    <option key={rate.id} value={rate.id}>
                      {rate.room_category_name} / {rate.name} ({rate.rate_code})
                    </option>
                  ))}
                </select>
              </label>
              <label className="rmo-formField">
                <span>{t("From")}</span>
                <input
                  className="rmo-input"
                  type="date"
                  value={form.date_from}
                  onChange={(event) => updateForm("date_from", event.target.value)}
                />
              </label>
              <label className="rmo-formField">
                <span>{t("To")}</span>
                <input
                  className="rmo-input"
                  type="date"
                  value={form.date_to}
                  onChange={(event) => updateForm("date_to", event.target.value)}
                />
              </label>
            </div>
            {selectedRoomRate ? (
              <div className="rmo-marketRefGuardrail">
                {t("Currency")}: <strong>{selectedRoomRate.currency}</strong>
                <span>/</span>
                {t("Allowed range")}: <strong>{formatMoney(selectedRoomRate.min_price_amount, selectedRoomRate.currency, locale)} - {formatMoney(selectedRoomRate.max_price_amount, selectedRoomRate.currency, locale)}</strong>
              </div>
            ) : null}
          </section>

          <section className="rmo-marketRefStep">
            <div className="rmo-marketRefStepHead">
              <span>3</span>
              <div>
                <strong>{t("Calculation")}</strong>
                <small>{t("First combine the market prices, then place your own rate relative to that result.")}</small>
              </div>
            </div>
            <div className="rmo-marketRefFormulaGrid">
              <label className="rmo-formField">
                <span>{t("Combine competitor prices using")}</span>
                <select
                  className="rmo-select"
                  value={form.combine_method}
                  onChange={(event) => updateForm("combine_method", event.target.value)}
                >
                  <option value="average">{t("Average")}</option>
                  <option value="min">{t("Minimum")}</option>
                  <option value="max">{t("Maximum")}</option>
                </select>
              </label>
              <label className="rmo-formField">
                <span>{t("Position my price")}</span>
                <select
                  className="rmo-select"
                  value={form.adjustment_type}
                  onChange={(event) => updateForm("adjustment_type", event.target.value)}
                >
                  <option value="equal">{t("Equal to the reference")}</option>
                  <option value="above">{t("Above the reference")}</option>
                  <option value="below">{t("Below the reference")}</option>
                </select>
              </label>
              <label className="rmo-formField">
                <span>{t("Adjustment type")}</span>
                <select
                  className="rmo-select"
                  value={form.adjustment_unit}
                  disabled={form.adjustment_type === "equal"}
                  onChange={(event) => updateForm("adjustment_unit", event.target.value)}
                >
                  <option value="absolute">{t("Fixed amount")}</option>
                  <option value="percent">{t("Percent")}</option>
                </select>
              </label>
              <label className="rmo-formField">
                <span>{t("Adjustment value")}</span>
                <div className="rmo-inputRow">
                  <input
                    className="rmo-input rmo-inputGrow"
                    type="number"
                    min="0"
                    step={form.adjustment_unit === "percent" ? "0.1" : "0.01"}
                    value={form.adjustment_type === "equal" ? "0" : form.adjustment_value}
                    disabled={form.adjustment_type === "equal"}
                    onChange={(event) => updateForm("adjustment_value", event.target.value)}
                  />
                  <span className="rmo-inputSuffix">
                    {form.adjustment_unit === "percent" ? "%" : selectedRoomRate?.currency || "EUR"}
                  </span>
                </div>
              </label>
            </div>
          </section>
        </div>

        <div className="rmo-marketRefActions">
          <div>
            <strong>{t("Nothing changes until you press Save and apply.")}</strong>
            <span>{t("Preview and Save reference are read-only for your room prices.")}</span>
          </div>
          <button
            type="button"
            className="rmo-btn rmo-btn--accent"
            disabled={Boolean(busy)}
            onClick={handlePreview}
          >
            {busy === "preview" ? t("Calculating...") : t("Preview result")}
          </button>
        </div>
      </div>

      {error ? <div className="rmo-errorBox">{error}</div> : null}
      {notice ? <div className="rmo-marketRefNotice">{notice}</div> : null}

      {preview ? (
        <div className="rmo-surface rmo-marketRefPreview">
          <div className="rmo-head">
            <div>
              <h3 className="rmo-headTitle">{t("Preview before applying")}</h3>
              <div className="rmo-headSubtitle">
                {buildFormulaLabel(preview, t)} / {previewDaysWithReference} {t("days with market price")}
              </div>
            </div>
            <span className="rmo-marketRefStepBadge isReady">{t("Ready to review")}</span>
          </div>
          <div className="rmo-marketRefPreviewBody">
            <MarketReferenceChart reference={preview} locale={locale} t={t} />
            <ReferenceDaysTable reference={preview} locale={locale} t={t} />
          </div>
          <div className="rmo-marketRefDecisionBar">
            <div>
              <strong>{t("Review the curve and daily values before deciding.")}</strong>
              <span>{t("Save keeps the snapshot for later. Save and apply writes the resulting prices now.")}</span>
            </div>
            <div className="rmo-inlineActions">
              <button
                type="button"
                className="rmo-btn"
                disabled={Boolean(busy) || !canApplyMarketPrices || Boolean(preview.id)}
                onClick={() => handleSave(false)}
              >
                {preview.id
                  ? t("Reference saved")
                  : busy === "save"
                    ? t("Saving...")
                    : t("Save reference")}
              </button>
              <button
                type="button"
                className="rmo-btn rmo-btn--accent"
                disabled={Boolean(busy) || !canApplyMarketPrices || !previewHasPendingDays}
                onClick={() => handleSave(true)}
              >
                {busy === "apply"
                  ? progress
                    ? t("Applying {completed}/{total}...", progress)
                    : t("Preparing application...")
                  : preview.id
                    ? t("Apply saved prices")
                    : t("Save and apply prices")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function RmsMarketPriceReferenceLibrary({
  businessUnitId,
  canApplyMarketPrices,
  t,
  locale,
  refreshVersion = 0,
}) {
  const [references, setReferences] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [reference, setReference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selectedDates, setSelectedDates] = useState([]);

  const loadReferences = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const rows = await fetchRmsMarketReferences(businessUnitId);
      setReferences(rows);
      setSelectedId((current) => current || rows[0]?.id || null);
    } catch (nextError) {
      setError(getErrorMessage(nextError, t("Could not load saved market references.")));
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, t]);

  useEffect(() => {
    loadReferences();
  }, [loadReferences, refreshVersion]);

  useEffect(() => {
    let cancelled = false;
    const loadDetail = async () => {
      if (!selectedId) {
        setReference(null);
        setSelectedDates([]);
        return;
      }
      try {
        setDetailLoading(true);
        setError("");
        const nextReference = await fetchRmsMarketReference(selectedId, businessUnitId);
        if (!cancelled) {
          setReference(nextReference);
          setSelectedDates([]);
        }
      } catch (nextError) {
        if (!cancelled) {
          setError(getErrorMessage(nextError, t("Could not load this market reference.")));
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [businessUnitId, selectedId, t]);

  const approvableDates = useMemo(
    () => getApprovableMarketReferenceDates(reference),
    [reference]
  );

  const toggleSelectedDate = (stayDate) => {
    setSelectedDates((current) => {
      const next = new Set(current);
      if (next.has(stayDate)) next.delete(stayDate);
      else next.add(stayDate);
      return normalizeMarketReferenceDateSelection(reference, [...next]);
    });
  };

  const handleApply = async () => {
    if (!reference || !canApplyMarketPrices) return;
    const approvedStayDates = normalizeMarketReferenceDateSelection(
      reference,
      selectedDates
    );
    if (!approvedStayDates.length) {
      setError(t("Select at least one date to approve."));
      return;
    }
    try {
      setApplying(true);
      setError("");
      setNotice("");
      const result = await applyRmsMarketReference(reference, {
        stayDates: approvedStayDates,
        onProgress: setProgress,
      });
      setReference(result.reference);
      setSelectedDates([]);
      setNotice(
        result.errors.length
          ? t("The reference remains partially applied: {count} dates failed.", {
              count: result.errors.length,
            })
          : t("The selected prices were applied.")
      );
      await loadReferences();
    } catch (nextError) {
      setError(getErrorMessage(nextError, t("Could not apply this market reference.")));
    } finally {
      setApplying(false);
      setProgress(null);
    }
  };

  return (
    <div className="rmo-surface rmo-marketRefLibrary">
      <div className="rmo-head">
        <div>
          <h3 className="rmo-headTitle">{t("Saved market references")}</h3>
          <div className="rmo-headSubtitle">
            {t("Each reference preserves the competitor offers, calculation and daily snapshot used at that moment.")}
          </div>
        </div>
        <button type="button" className="rmo-btn" disabled={loading} onClick={loadReferences}>
          {loading ? t("Refreshing...") : t("Refresh")}
        </button>
      </div>

      {error ? <div className="rmo-errorBox">{error}</div> : null}
      {notice ? <div className="rmo-marketRefNotice">{notice}</div> : null}

      {loading && !references.length ? (
        <div className="rmo-empty">{t("Loading saved market references...")}</div>
      ) : !references.length ? (
        <div className="rmo-empty">{t("No market references have been saved yet.")}</div>
      ) : (
        <div className="rmo-marketRefLibraryLayout">
          <aside className="rmo-marketRefList" aria-label={t("Saved references")}>
            {references.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`rmo-marketRefListItem${String(item.id) === String(selectedId) ? " isActive" : ""}`}
                onClick={() => setSelectedId(item.id)}
              >
                <span className="rmo-marketRefListTop">
                  <strong>{item.name || `${t("Reference")} #${item.id}`}</strong>
                  <span className={`rmo-marketRefStatus is-${item.status}`}>{t(item.status)}</span>
                </span>
                <span>{item.room_rate?.room_category_name} / {item.room_rate?.name}</span>
                <small>{formatShortDate(item.date_from, locale)} - {formatShortDate(item.date_to, locale)} / {buildFormulaLabel(item, t)}</small>
              </button>
            ))}
          </aside>

          <main className="rmo-marketRefDetail">
            {detailLoading || !reference ? (
              <div className="rmo-empty">{t("Loading reference detail...")}</div>
            ) : (
              <>
                <div className="rmo-marketRefDetailHead">
                  <div>
                    <h4>{reference.name || `${t("Reference")} #${reference.id}`}</h4>
                    <p>{reference.room_rate?.room_category_name} / {reference.room_rate?.name} / {buildFormulaLabel(reference, t)}</p>
                    {approvableDates.length ? (
                      <div className="rmo-marketRefSelectionActions">
                        <button
                          type="button"
                          className="rmo-btn"
                          disabled={applying}
                          onClick={() => setSelectedDates(approvableDates)}
                        >
                          {t("Select all pending dates")}
                        </button>
                        <button
                          type="button"
                          className="rmo-btn"
                          disabled={applying || !selectedDates.length}
                          onClick={() => setSelectedDates([])}
                        >
                          {t("Clear selection")}
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="rmo-btn rmo-btn--accent"
                    disabled={
                      applying ||
                      !canApplyMarketPrices ||
                      reference.status === "applied" ||
                      !selectedDates.length
                    }
                    onClick={handleApply}
                  >
                    {applying
                      ? progress
                        ? t("Applying {completed}/{total}...", progress)
                        : t("Applying...")
                      : t("Apply selected prices ({count})", {
                          count: selectedDates.length,
                        })}
                  </button>
                </div>
                <MarketReferenceChart reference={reference} locale={locale} t={t} />
                <ReferenceDaysTable
                  reference={reference}
                  locale={locale}
                  t={t}
                  selectedDates={selectedDates}
                  onToggleDate={toggleSelectedDate}
                />
              </>
            )}
          </main>
        </div>
      )}
    </div>
  );
}
